import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { computed, inject } from "@angular/core";
import { s25Resource } from "../../../util/s25.resource";
import { EngagementService, EngagementSystemAlert } from "../engagement-metrics/engagement.service";
import { ProductLicenseStore } from "../../../stores/product.license.store";
import { LoginStore } from "../../../stores/login.store";
import { AriaLive } from "../../../services/aria.live.service";

const SESSION_STORAGE_DISMISSED_KEY = "connect25_system_alerts_dismissed";

function parseDismissedIds(): Set<number> {
    try {
        const rawJson = sessionStorage.getItem(SESSION_STORAGE_DISMISSED_KEY);
        if (!rawJson) return new Set();
        const parsedIds = JSON.parse(rawJson) as number[];
        return new Set(parsedIds.filter((id) => typeof id === "number"));
    } catch {
        return new Set();
    }
}

function persistDismissedIds(dismissedIds: Set<number>): void {
    sessionStorage.setItem(SESSION_STORAGE_DISMISSED_KEY, JSON.stringify([...dismissedIds]));
}

export function bySortOrder(left: EngagementSystemAlert, right: EngagementSystemAlert): number {
    const orderDelta = (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
    if (orderDelta !== 0) return orderDelta;
    return (left.engagementSystemAlertId ?? 0) - (right.engagementSystemAlertId ?? 0);
}

type State = {
    openAllAlertsTick: number;
    /** Shared collapse state for the banner alert overlay; collapsed by default. Toggled from the breadcrumb bar. */
    bannerCollapsed: boolean;
    /** Session-scoped acknowledgements; only affect banners / queued notices. */
    dismissedIds: Set<number>;
};

export const Connect25SystemAlertsCoordinatorStore = signalStore(
    { providedIn: "root" },
    withState<State>({ openAllAlertsTick: 0, bannerCollapsed: true, dismissedIds: parseDismissedIds() }),
    withProps(() => ({
        _productLicenseStore: inject(ProductLicenseStore),
        _loginStore: inject(LoginStore),
    })),
    withProps((store) => ({
        _alerts: s25Resource({
            // Reactive on login so the list auto-reloads when the user logs in / out.
            params: () => store._loginStore.loggedIn(),
            loader: async () => {
                await store._productLicenseStore.ensureLoaded();
                if (!store._productLicenseStore.hasStudentEngagement()) {
                    return [] as EngagementSystemAlert[];
                }
                return EngagementService.getActiveSystemAlerts();
            },
        }),
    })),
    withComputed((store) => {
        const filteredAlerts = computed(() => {
            const dismissed = store.dismissedIds();
            return (store._alerts.value() ?? [])
                .filter(
                    (alertDoc) =>
                        alertDoc.engagementSystemAlertId != null && !dismissed.has(alertDoc.engagementSystemAlertId),
                )
                .sort(bySortOrder);
        });
        const bannerAlerts = computed(() =>
            filteredAlerts().filter((alertDoc) => alertDoc.uxType?.toUpperCase() === "BANNER"),
        );
        const modalAlerts = computed(() =>
            filteredAlerts().filter((alertDoc) => alertDoc.uxType?.toUpperCase() !== "BANNER"),
        );
        return {
            filteredAlerts,
            bannerAlerts,
            modalAlerts,
            /** Every active alert, ignoring session acknowledgements — view-all is a reference view, not an inbox. */
            allActiveAlerts: computed(() =>
                (store._alerts.value() ?? [])
                    .filter((alertDoc) => alertDoc.engagementSystemAlertId != null)
                    .sort(bySortOrder),
            ),
            /** Unacknowledged banner alert count, so the breadcrumb bar can show its toggle. */
            bannerAlertCount: computed(() => bannerAlerts().length),
            alertsLoading: computed(() => store._alerts.isLoading()),
            hasStudentEngagement: computed(() => store._productLicenseStore.hasStudentEngagement()),
        };
    }),
    withMethods((store) => ({
        requestOpenAllAlerts() {
            patchState(store, { openAllAlertsTick: store.openAllAlertsTick() + 1 });
        },
        toggleBannerCollapsed() {
            patchState(store, { bannerCollapsed: !store.bannerCollapsed() });
        },
        /** Idempotent so redundant dismissal paths (outside click, Escape) can never re-open the overlay. */
        collapseBanner() {
            patchState(store, { bannerCollapsed: true });
        },
        dismiss(alertId: number) {
            const nextDismissed = new Set(store.dismissedIds());
            nextDismissed.add(alertId);
            patchState(store, { dismissedIds: nextDismissed });
            persistDismissedIds(nextDismissed);
            AriaLive.announce("Notice acknowledged", false);
        },
    })),
);

export type Connect25SystemAlertsCoordinatorStore = InstanceType<typeof Connect25SystemAlertsCoordinatorStore>;
